<script lang="ts" setup>
import {ref} from 'vue'
import {useMenu} from '~/composables/useMenu'

interface Props {
  persistent?: boolean
  fullscreen?: boolean
  noPadding?: boolean
  closable?: boolean
  title?: string
  withTop?: boolean
  backgroundColor?: string
  gap?: string
  zIndex?: string | number,
  mobilePosition?: 'bottom' | 'top'
}

const props = withDefaults(defineProps<Props>(), {
  persistent: false,
  fullscreen: false,
  noPadding: false,
  closable: false,
  title: "",
  withTop: false,
  backgroundColor: 'var(--white)',
  gap: '1rem',
  zIndex: 1001,
  mobilePosition: 'bottom'
})

const visible = defineModel<boolean>('visible')

const modal = ref<HTMLElement | null>(null)
const backdrop = ref<HTMLElement | null>(null)

useMenu(visible, modal, true, props.persistent ?? false)

const handleEsc = () => {
  if (!props.persistent) {
    visible.value = false
  }
}

watch(visible, (val) => {
  if (val) {
    nextTick(() => backdrop.value?.focus())
  }
}, {immediate: true})

const close = () => {
  visible.value = false
}

</script>

<template>
  <teleport to="body">
    <transition name="fade">
      <div v-if="visible"
           ref="backdrop"
           class="dialog-custom"
           :class="{'dialog-custom-with-top': withTop}"
           :style="{'z-index': zIndex}"
           tabindex="-1"
           role="dialog"
           aria-modal="true"
           @keydown.esc="handleEsc"
      >
        <div ref="modal"
             class="dialog-custom-modal"
             :data-mobile-position="mobilePosition"
             :style="{'margin-top': withTop && fullscreen ? 'auto' : undefined, '--dialog-bg': backgroundColor, '--dialog-gap': gap}"
             :class="{'dialog-custom-modal--fullscreen': fullscreen,
                      'dialog-custom-modal--no-padding': noPadding
        }"
        >
          <slot name="title" :close="close">
            <header v-if="title || closable" class="dialog-custom-modal-header">
              <span class="dialog-custom-modal-header-title">{{ title }}</span>
              <ui-custom-button v-if="closable" :color="'secondary'" :types="['round']" :size="'lg'" @click="close"
                                :icon="'core-close'" :icon-color="'cool-grey'" :icon-size="12"/>
            </header>
          </slot>
          <div class="dialog-custom-modal-content">
            <slot/>
          </div>
          <footer v-if="$slots.footer">
            <slot name="footer"/>
          </footer>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<style scoped lang="scss">
@use 'assets/css/tokens/colors' as *;
@use 'assets/css/tokens/typography' as *;

.dialog-custom {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #273A6466 !important;
  outline: none;
}

.dialog-custom-with-top {
  top: calc(1rem * 2 + max(clamp(30px, 4vw, 48px), 2.5rem));

  @media (max-width: 959px) {
    top: calc(1rem * 2 + max(clamp(30px, 4vw, 48px), 2rem));
  }
}

.dialog-custom-modal {
  display: flex;
  flex-direction: column;
  gap: var(--dialog-gap);
  align-items: stretch;
  background-color: var(--dialog-bg);
  box-shadow: 0 14px 44px 0 #2531456B;
  padding: 1.5rem 1.625rem;
  border-radius: 12px;

  @media (max-width: 959px) {
    width: 100vw;

    &[data-mobile-position="bottom"] {
      margin-top: auto;
      margin-bottom: 0;
      border-radius: 12px 12px 0 0;
      box-shadow: none;
    }

    &[data-mobile-position="top"] {
      margin-bottom: auto;
      margin-top: 0;
      border-top: 1px solid $color-border;
      border-radius: 0 0 12px 12px;
      box-shadow: none;
    }
  }

  &--no-padding {
    padding: 0 !important;
  }

  &--fullscreen {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    border-radius: 0;
    box-shadow: none;
  }
}


.dialog-custom-modal-header {
  display: flex;
  position: relative;
  flex-direction: row;
  gap: .5rem;
  align-items: center;
  justify-content: space-between;
}

.dialog-custom-modal-header-title {
  font-size: $font-size-22;
  line-height: $line-height-42;
  font-weight: $font-weight-semibold;
  color: $color-dark;
}

.dialog-custom-modal-content {
  overflow-y: auto;
  max-height: 100%;
  scrollbar-width: none;
}


.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
